import React, { useEffect, useState } from 'react'
import { AiFeature } from '../../../../../models/AiFeature'
import { useAppSelector } from '../../../../../store/hooks'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { Button } from '../../../../../components/Button/Button'
import { AddResultCustomToCRMModal, AddResultDefaultToCRMModal, CRMFormValues } from '../../../../../views/MeetingSummary/CRM/V2/AddResultDefaultToCRMModal'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useSaveResultToCRMMutation } from '../../../../../store/integrations/integrations'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { AIAgentResult } from '../../../../../models/AIAgent'
import { TrackEvent, useTracking } from '../../common/tracking'
import { Me, User } from '../../../../../models/User'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useIntegrationExportConfirmation } from '../../../../../components/ConfirmationDialog/useIntegrationExportConfirmation'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../../../common/integrations'

export interface FormToCRMButtonProps {
  aiFeature: AiFeature
  serviceId: string
  formResult: AIAgentResult
  contact: User | null
  currentUser: Me | null
}

export const AddFormToCRMButton: React.FC<FormToCRMButtonProps> = ({ aiFeature, serviceId, formResult, contact, currentUser }) => {
  const accountId = useAppSelector(selectAccountId)

  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature
  const [show, setShow] = useState(false)
  const roomSession = useAppSelector(selectRoomSession)
  const [triggerSaveResults, { isSuccess, isLoading, reset }] = useSaveResultToCRMMutation()
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useIntegrationExportConfirmation({ roomSession })

  const [contactObject, setContactObject] = useState<string>('Contact')
  const { tracked } = useTracking()

  useEffect(() => {
    if (isSuccess) {
      reset()
    }
  }, [isSuccess, reset])

  const handleSave = (data: CRMFormValues) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    if (!serviceIntegration) {
      return
    }

    const integrationContactObject =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT].objectName
    if (serviceId === 'salesforce') {
      setContactObject(integrationContactObject)
    }
    _handleSave(data)
  }

  const _handleSave = tracked(
    async (data: CRMFormValues) => {
      await runMutationWithConfirmation(async () => {
        triggerSaveResults({
          accountId,
          connector: serviceId,
          body: {
            type: 'aiAgentResult',
            ...(roomSession && { roomId: roomSession.roomId }),
            ...(roomSession && { sessionId: roomSession.id }),
            resultId: formResult.id,
            relatedTo: { id: data.externalUserId, type: contactObject },
          },
        })
      })
    },
    { eventName: TrackEvent.SAVE_FORM_TO_CRM },
    {
      eventDetailsExtractor: (data: CRMFormValues) => {
        return {
          ...(roomSession && { roomId: roomSession.roomId }),
          ...(roomSession && { sessionId: roomSession.id }),
          formResultId: formResult?.result?.id,
          contactId: contact?.id,
          externalUserId: data?.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const userHasIntegration = currentUserIntegrations?.some(({ serviceId: sId }) => serviceId === sId)
  const aiFeatureHasIntegration = aiFeatureIntegrations.some(({ serviceId: sId }) => serviceId === sId)
  if (!userHasIntegration || !aiFeatureHasIntegration) {
    return null
  }
  const serviceIdLabel = serviceId[0].toUpperCase() + serviceId.slice(1)
  return (
    <div className='d-none d-md-flex'>
      {show && (
        <AddResultDefaultToCRMModal
          aiFeature={aiFeature}
          result={formResult}
          show={show}
          onHide={() => setShow(false)}
          serviceId={serviceId}
          handleSaveResult={handleSave}
          isSuccess={isSuccess}
          isLoading={isLoading || accountIsLoading}
        />
      )}
      <Tooltip className='paragraph-small' title={`Save to ${serviceIdLabel}`} placement='top' offset={[0, 10]}>
        <Button className='px-2' variant='header' onClick={() => setShow((show) => !show)}>
          <IntegrationIcon serviceId={serviceId as IntegrationServiceId} className='crm-icon' fill='var(--zocks-gray-900)' />
        </Button>
      </Tooltip>
      <ConfirmationModal />
    </div>
  )
}

export interface FormToCustomButtonProps {
  aiFeature: AiFeature
  serviceId: string
  formResult: AIAgentResult
  contact: User
}

export const AddFormToCustomButton: React.FC<FormToCustomButtonProps> = ({ aiFeature, serviceId, formResult, contact }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useIntegrationExportConfirmation({ roomSession })
  const { integrationInfos: aiFeatureIntegrations } = aiFeature
  const [show, setShow] = useState(false)
  const [triggerSaveResults, { isSuccess, isLoading, reset }] = useSaveResultToCRMMutation()

  useEffect(() => {
    if (isSuccess) {
      reset()
    }
  }, [isSuccess, reset])

  const handleSave = () => {
    _handleSave()
  }

  const _handleSave = async () => {
    await runMutationWithConfirmation(async () => {
      await triggerSaveResults({
        accountId,
        connector: serviceId,
        body: {
          type: 'aiAgentResult',
          ...(roomSession && { roomId: roomSession.roomId }),
          ...(roomSession && { sessionId: roomSession.id }),
          resultId: formResult.id,
          relatedTo: { id: contact.id },
        },
      })
    })
  }

  const aiFeatureHasIntegration = aiFeatureIntegrations.some(({ serviceId: sId }) => serviceId === sId)
  if (!aiFeatureHasIntegration) {
    return null
  }
  return (
    <div className='d-none d-md-flex'>
      {show && (
        <AddResultCustomToCRMModal
          aiFeature={aiFeature}
          result={formResult}
          show={show}
          onHide={() => setShow(false)}
          serviceId={serviceId}
          handleSaveResult={handleSave}
          isSuccess={isSuccess}
          isLoading={isLoading || accountIsLoading}
          contactId={contact.id}
        />
      )}
      <Tooltip className='paragraph-small' title={`Save to your system.`} placement='top' offset={[0, 10]}>
        <Button className='px-2' variant='header' onClick={() => setShow((show) => !show)}>
          <i className='bi bi-cloud-upload crm-icon'></i>
        </Button>
      </Tooltip>
      <ConfirmationModal />
    </div>
  )
}
